import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import { filterValidCta, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import Cta from "@qlik/qlik/src/components/Cta";
import RichText from "@qlik/qlik/src/components/RichText";

/**
 * CardWrapper component.
 * Wraps entire card in a link for Resource Blog type.
 * @param {Object} props - The props for the CardWrapper component.
 * @returns {JSX.Element} - The CardWrapper component.
 */

const CardWrapper = ({ isWholeCardClickable, className, linkData, children }) =>
    isWholeCardClickable && !!linkData && (linkData.internalLink?.slug || linkData.externalUrl) ? (
        <Cta data={linkData} className={`block hover:no-underline ${className}`}>
            {children}
        </Cta>
    ) : (
        <div className={className}>{children}</div>
    );

/**
 * ImageCard component.
 * Renders a single image card with different styles based on the type.
 * @param {Object} props - The props for the ImageCard component.
 * @returns {JSX.Element} - The ImageCard component.
 */
const ImageCard = ({ data }) => {
    const { title, cta, body, media } = data || {};

    const ctaList = filterValidCta(cta);
    let isWholeCardClickable;
    if (ctaList.length === 1) {
        isWholeCardClickable = true;
    }

    return (
        <CardWrapper
            className={`group border border-stone/50 p-0 bg-white rounded hover:no-underline group/resource-card lg:hover:shadow-lg  transition-shadow duration-300 flex flex-col w-full overflow-hidden ${
                isWholeCardClickable ? "after:hidden h-full" : ""
            }`}
            isWholeCardClickable={isWholeCardClickable}
            linkData={ctaList[0]}
        >
            <div className="flex flex-col pb-2 lg:pb-4.5 p-7.5 lg:p-10 flex-1">
                {title && (
                    <h3
                        className="mb-3.75 title-4"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                    />
                )}
                {body?.raw && <RichText data={data} className="mb-8 lg:mb-7.5" />}

                {/* pro links - only render as links if card is not a link already */}
                <div className="mt-auto flex flex-col gap-3.75">
                    {ctaList.map((linkData) =>
                        isWholeCardClickable ? (
                            <span
                                key={linkData.id}
                                className="green-link text-sm lg:group-hover/resource-card:text-dark-green lg:group-hover/resource-card:after:arrow-green-dark lg:group-hover/resource-card:after:translate-x-0.75"
                            >
                                {linkData.text}
                            </span>
                        ) : (
                            <Cta key={linkData.id} className="text-sm" data={linkData} />
                        )
                    )}
                </div>
            </div>
            {(media?.[0]?.desktopMedia.length || media?.[0]?.mobileMedia.length) > 0 && (
                <div className="overflow-hidden  h-[190px] lg:h-45 mt-12.5 lg:mt-10 rounded-br rounded-tl ml-7.5 lg:ml-10 shadow-xlMedium">
                    <Media
                        media={media[0]}
                        className="w-full h-full object-cover rounded-tl rounded-br group-hover:scale-105 transition-all ease-linear duration-200"
                    />
                </div>
            )}
        </CardWrapper>
    );
};

export default ImageCard;
